// @view/authentication/PasswordResetRequest.view.tsx

import React from "react";
import * as styles from "@styles/PasswordReset.css";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import * as Option from "@utils/Option";

import { ok } from "@ps-bridge/ok";
import * as IO from "@ps-bridge/interop/io";
import type { PasswordResetRequestViewProps } from "@ps-bridge/contracts/passwordResetRequest";
import PasswordResetLogoPanel from "@view/panels/authentication/PasswordResetLogo.panel";
import PasswordResetNoticePanel from "@view/panels/authentication/PasswordResetNotice.panel";
import { onInputAction } from "@view/effects/inputHandlers";

const PasswordResetRequestView: React.FC<PasswordResetRequestViewProps> = ({
    email,
    onEmailChange,
    onSubmit,
    onNavigateToLogin,
    logoPhase,
    logoUri,
    noticePhase,
}) => {
    return (
        <div
            className={styles.page}
            data-testid="page-password-reset-request"
            data-page-state="ready"
        >
            <Card className={styles.card}>
                <CardContent>
                    <PasswordResetLogoPanel phase={logoPhase} logoUri={logoUri} />
                    <CardTitle className={styles.title}>Reset your password</CardTitle>
                    <p className={styles.subtitle}>
                        Enter the email associated with your account and we will send you a reset link.
                    </p>
                    <div className={styles.field}>
                        <Input
                            type="email"
                            placeholder="Email"
                            value={Option.fold(email, () => "", (value) => value)}
                            onChange={onInputAction(onEmailChange)}
                            autoComplete="email"
                            data-testid="password-reset-request-email"
                        />
                    </div>
                    <Button
                        className={styles.button}
                        onClick={() => {
                            IO.run(onSubmit(ok));
                        }}
                        asChild={Option.none()}
                        data-testid="password-reset-request-submit"
                    >
                        Send reset link
                    </Button>
                    <PasswordResetNoticePanel phase={noticePhase} />
                    <Button
                        variant="ghost"
                        className={styles.textButton}
                        onClick={() => IO.run(onNavigateToLogin(ok))}
                        asChild={Option.none()}
                        data-testid="password-reset-request-back-to-login"
                    >
                        Back to login
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default PasswordResetRequestView;
